import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { companyService } from '../../services/companyService';
import { productService } from '../../services/productService';

// Import all components
import { CompaniesList } from './CompaniesList';
import { CompanyDetailsView } from './CompanyDetailsView';
import { CompanyFormModal } from './CompanyFormModal';
import { CompanyFilters } from './CompanyFilters';
import { DeleteModal } from '../shared/DeleteModal';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorAlert } from '../shared/ErrorAlert';

// This will be imported from your products module
// import ProductsContent from '../products/ProductsContent';

const CompaniesContent = ({ onNavigateToProducts }) => {
    const { hasPermission } = useAuth();

    // State Management
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // UI State
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [showFormModal, setShowFormModal] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ open: false, company: null });
    const [actionLoading, setActionLoading] = useState(false);
    const [companyProducts, setCompanyProducts] = useState([]);

    // Filter State
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedScope, setSelectedScope] = useState('ALL');
    const [activeFilter, setActiveFilter] = useState('ALL');

    // Fetch initial data
    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await companyService.getAll();

            if (response.success && response.data) {
                // Fetch product count for each company
                const companiesWithProducts = await Promise.all(
                    response.data.map(async (company) => {
                        try {
                            const productsResponse = await productService.getByCompany(company.id);
                            return {
                                ...company,
                                productCount: productsResponse.data?.length || 0
                            };
                        } catch {
                            return { ...company, productCount: 0 };
                        }
                    })
                );
                setCompanies(companiesWithProducts);
            } else {
                throw new Error(response.message || 'Failed to load companies');
            }
        } catch (err) {
            console.error('Error fetching companies:', err);
            setError(err.message || 'Failed to load companies');
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanyProducts = async (companyId) => {
        try {
            const response = await productService.getByCompany(companyId);
            if (response.success && response.data) {
                setCompanyProducts(response.data);
            } else {
                setCompanyProducts([]);
            }
        } catch (err) {
            console.error('Error fetching company products:', err);
            setCompanyProducts([]);
        }
    };

    // CRUD Operations
    const handleCreateCompany = async (companyData) => {
        try {
            setActionLoading(true);
            const response = await companyService.create(companyData);

            if (response.success) {
                await fetchCompanies();
                setShowFormModal(false);
                setEditingCompany(null);
            } else {
                throw new Error(response.message || 'Failed to create company');
            }
        } catch (err) {
            console.error('Error creating company:', err);
            setError(err.message || 'Failed to create company');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateCompany = async (companyData) => {
        if (!editingCompany) return;

        try {
            setActionLoading(true);
            const response = await companyService.update(editingCompany.id, companyData);

            if (response.success) {
                await fetchCompanies();
                setShowFormModal(false);
                setEditingCompany(null);

                // Update selected company if it's the one being edited
                if (selectedCompany?.id === editingCompany.id) {
                    setSelectedCompany({ ...selectedCompany, ...companyData });
                }
            } else {
                throw new Error(response.message || 'Failed to update company');
            }
        } catch (err) {
            console.error('Error updating company:', err);
            setError(err.message || 'Failed to update company');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteCompany = async () => {
        if (!deleteModal.company) return;

        try {
            setActionLoading(true);
            const response = await companyService.delete(deleteModal.company.id);

            if (response.success) {
                await fetchCompanies();
                setDeleteModal({ open: false, company: null });

                // Clear selected company if it's the one being deleted
                if (selectedCompany?.id === deleteModal.company.id) {
                    setSelectedCompany(null);
                }
            } else {
                throw new Error(response.message || 'Failed to delete company');
            }
        } catch (err) {
            console.error('Error deleting company:', err);
            setError(err.message || 'Failed to delete company');
        } finally {
            setActionLoading(false);
        }
    };

    // Filter companies
    const filteredCompanies = companies.filter(company => {
        const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            company.code.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesScope = selectedScope === 'ALL' || company.businessScope === selectedScope;
        const matchesActive = activeFilter === 'ALL' ||
            (activeFilter === 'ACTIVE' && company.active) ||
            (activeFilter === 'INACTIVE' && !company.active);

        return matchesSearch && matchesScope && matchesActive;
    });

    // UI Handlers
    const handleAddCompany = () => {
        setEditingCompany(null);
        setShowFormModal(true);
    };

    const handleEditCompany = (company) => {
        setEditingCompany(company);
        setShowFormModal(true);
    };

    const handleViewCompany = async (company) => {
        setSelectedCompany(company);
        await fetchCompanyProducts(company.id);
    };

    const handleViewProducts = (company) => {
        // Navigate to products view with company filter
        if (onNavigateToProducts) {
            onNavigateToProducts(company.id, company.name);
        }
    };

    // Show company details view if selected
    if (selectedCompany) {
        return (
            <CompanyDetailsView
                company={selectedCompany}
                products={companyProducts}
                onBack={() => {
                    setSelectedCompany(null);
                    setCompanyProducts([]);
                }}
                onEdit={handleEditCompany}
                onViewProducts={handleViewProducts}
            />
        );
    }

    // Show loading state
    if (loading) {
        return <LoadingSpinner message="Loading companies..." />;
    }

    // Main companies list view
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Companies</h2>
                    <p className="text-gray-600 mt-1">
                        {filteredCompanies.length} of {companies.length} companies
                    </p>
                </div>
                {hasPermission('COMPANIES:CREATE') && (
                    <button
                        onClick={handleAddCompany}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        Add Company
                    </button>
                )}
            </div>

            {/* Error Alert */}
            {error && <ErrorAlert error={error} onRetry={fetchCompanies} />}

            {/* Filters */}
            <CompanyFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                selectedScope={selectedScope}
                onScopeChange={setSelectedScope}
                activeFilter={activeFilter}
                onActiveFilterChange={setActiveFilter}
            />

            {/* Companies List */}
            <CompaniesList
                companies={filteredCompanies}
                onView={handleViewCompany}
                onEdit={handleEditCompany}
                onDelete={(company) => setDeleteModal({ open: true, company })}
                onViewProducts={handleViewProducts}
                loading={loading}
                hasPermission={hasPermission}
            />

            {/* Company Form Modal */}
            <CompanyFormModal
                isOpen={showFormModal}
                onClose={() => {
                    setShowFormModal(false);
                    setEditingCompany(null);
                }}
                onSubmit={editingCompany ? handleUpdateCompany : handleCreateCompany}
                company={editingCompany}
                loading={actionLoading}
            />

            {/* Delete Confirmation Modal */}
            <DeleteModal
                isOpen={deleteModal.open}
                onClose={() => setDeleteModal({ open: false, company: null })}
                onConfirm={handleDeleteCompany}
                itemName={deleteModal.company?.name}
                itemType="company"
                loading={actionLoading}
            />
        </div>
    );
};

export default CompaniesContent;
